import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isTop = !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTop
          ? "bg-gradient-to-b from-black/25 to-transparent"
          : "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-baseline gap-1 cursor-pointer flex-shrink-0">
          <span className={`font-heading text-xl font-bold transition-colors duration-300 ${isTop ? "text-white" : "text-foreground-900"}`}>
            ACBuy
          </span>
          <span className={`text-xs tracking-widest uppercase transition-colors duration-300 ${isTop ? "text-white/70" : "text-foreground-500"}`}>
            Spreadsheet
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-4">
          {/* Home */}
          <Link
            to="/"
            className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              isTop ? "text-white/90 hover:text-white" : "text-foreground-700 hover:text-foreground-900"
            }`}
          >
            Home
          </Link>

          {/* Categories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-sm transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                isTop ? "text-white/90 hover:text-white" : "text-foreground-700 hover:text-foreground-900"
              }`}
            >
              All Categories
              <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[720px] bg-background-50 rounded-xl border border-background-200/70 shadow-lg animate-slide-down overflow-hidden">
                <div className="grid grid-cols-4 gap-1 p-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img
                          src={cat.imageUrl}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <span className="text-sm text-foreground-800 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Shopping Guide */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${
              isTop ? "text-white/90 hover:text-white" : "text-foreground-700 hover:text-foreground-900"
            }`}
          >
            Shopping Guide
          </a>

          {/* acbuy spreadsheet */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm px-5 py-2 rounded-full transition-all whitespace-nowrap cursor-pointer border ${
              isTop
                ? "border-white/40 text-white hover:border-white/70 hover:bg-white/10"
                : "border-foreground-300 text-foreground-600 hover:text-foreground-900 hover:border-foreground-500"
            }`}
          >
            acbuy spreadsheet
          </a>

          {/* buy it now */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm bg-primary-500 text-white px-5 py-2 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            buy it now
            <i className="ri-arrow-right-line text-xs"></i>
          </a>
        </div>

        {/* Mobile Nav */}
        <div className="md:hidden flex items-center gap-2">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`text-xs px-3 py-1.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${
              isTop
                ? "bg-white/20 text-white backdrop-blur-sm"
                : "bg-primary-500 text-white"
            }`}
          >
            buy now
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`w-9 h-9 flex items-center justify-center cursor-pointer rounded-full transition-colors ${
              isTop ? "text-white hover:bg-white/10" : "text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-slide-down max-h-[70vh] overflow-y-auto">
          {/* Quick Links */}
          <div className="px-4 py-3 flex items-center gap-4 border-b border-background-200/40">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-home-4-line text-base"></i>
              Home
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line text-base"></i>
              Shopping Guide
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap ml-auto"
            >
              acbuy spreadsheet
            </a>
          </div>

          {/* Category Grid with Images */}
          <div className="px-3 py-3">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">
              Product Categories
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-sm text-foreground-800 group-hover:text-foreground-950 transition-colors leading-tight">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}